import React, { Fragment, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import XIcon from 'icons/X.icon';
import { getHasElementsInArray, safeParse } from 'utils';
import { getIsEditorValueEmpty } from 'components/RichTextEditorField/utils';
import ReplySuggestionsPlaceholder from './components/ReplySuggestionsPlaceholder';
import {
  getIsReplySuggestionShortened,
  getShortenedReplySuggestion,
} from '../../utils';
import ResolvePopup from 'components/ResolvePopup';

import './ReplySuggestions.styles.scss';

type ReplySuggestionsProps = {
  className?: string;
  message: string;
  suggestions: string[];
  loading?: boolean;
  onSelect: (suggestion: string) => void;
  onRemove: () => void;
};

const ReplySuggestions: React.FC<ReplySuggestionsProps> = (props) => {
  const { className, message, suggestions, loading, onSelect, onRemove } =
    props;

  const { t } = useTranslation();

  const isMessageEmpty = getIsEditorValueEmpty(safeParse(message));

  const classes = classNames('reply-suggestions', className);

  useEffect(() => {
    if (!isMessageEmpty) onRemove();
  }, [isMessageEmpty, onRemove]);

  const renderSuggestion = useCallback(
    (suggestion: string) => (
      <Button
        type="button"
        variant="outline"
        className="reply-suggestions__suggestion"
        onClick={(e) => {
          e.stopPropagation();
          onSelect(suggestion);
        }}
      >
        {getShortenedReplySuggestion(suggestion)}
      </Button>
    ),
    [onSelect],
  );

  if (loading) return <ReplySuggestionsPlaceholder className={classes} />;

  if (!isMessageEmpty || !getHasElementsInArray(suggestions)) return null;

  return (
    <div className={classes} role="group" aria-label={t('suggestedReplies')}>
      {suggestions.map((suggestion, index) => (
        <Fragment key={index}>
          {getIsReplySuggestionShortened(suggestion) ? (
            <ResolvePopup
              onHover
              preferUpwards
              centerOnVisibleTrigger
              popupContent={suggestion}
            >
              {renderSuggestion(suggestion)}
            </ResolvePopup>
          ) : (
            renderSuggestion(suggestion)
          )}
        </Fragment>
      ))}

      <Button
        type="button"
        variant="icon"
        className="reply-suggestions__dismiss"
        icon={XIcon}
        onClick={onRemove}
      />
    </div>
  );
};

export default ReplySuggestions;
